/** Κωδικοί προτύπου ενημέρωσης — ίδια τιμή με το API */
export const NotificationTypeCodes = {
  BinReceived: 'BIN_RECEIVED',
  CpDataUpdate: 'CP_DATA_UPDATE',
  HasBinYearly: 'HAS_BIN_YEARLY',
} as const;

export const NotificationResponseTypes = {
  YesNo: 'YES_NO',
  Form: 'FORM',
} as const;

/** Τιμή responseValue όταν συμπληρώθηκε η φόρμα — ίδια με το API */
export const NotificationFormCompletedValue = 'Form Completed';

export const NotificationStatus = {
  Draft: 1,
  Composed: 2,
  Sending: 3,
  Completed: 4,
  Closed: 5,
} as const;

export const NotificationRecipientStatus = {
  NoEmail: 1,
  Queued: 2,
  Failed: 3,
  Sent: 4,
  Responded: 5,
  TokenExpired: 6,
} as const;

export const notificationTypeOptions = [
  {
    value: NotificationTypeCodes.BinReceived,
    label: 'Παραλαβή κάδου',
    responseType: NotificationResponseTypes.YesNo,
  },
  {
    value: NotificationTypeCodes.CpDataUpdate,
    label: 'Επικαιροποίηση στοιχείων',
    responseType: NotificationResponseTypes.Form,
  },
  {
    value: NotificationTypeCodes.HasBinYearly,
    label: 'Έχετε κάδο;',
    responseType: NotificationResponseTypes.YesNo,
  },
];

export function notificationTypeLabel(typeCode?: string): string {
  return notificationTypeOptions.find((o) => o.value === typeCode)?.label ?? typeCode ?? '';
}

export function notificationResponseTypeLabel(responseType?: string): string {
  if (responseType === NotificationResponseTypes.YesNo) return 'Ναι / Όχι';
  if (responseType === NotificationResponseTypes.Form) return 'Φόρμα';
  return responseType ?? '';
}

export function notificationStatusLabel(statusId?: number): string {
  switch (statusId) {
    case NotificationStatus.Draft:
      return 'Προσχέδιο';
    case NotificationStatus.Composed:
      return 'Συντέθηκε';
    case NotificationStatus.Sending:
      return 'Σε αποστολή';
    case NotificationStatus.Completed:
      return 'Ολοκληρώθηκε η αποστολή';
    case NotificationStatus.Closed:
      return 'Κλειστή';
    default:
      return '';
  }
}

export function notificationResponseValueLabel(value?: string | null): string {
  if (value === NotificationFormCompletedValue || value === 'completed') {
    return 'Η φόρμα συμπληρώθηκε';
  }
  if (value === 'yes') return 'Ναι';
  if (value === 'no') return 'Όχι';
  return value ?? '';
}

export function notificationRecipientStatusLabel(statusId?: number): string {
  switch (statusId) {
    case NotificationRecipientStatus.NoEmail:
      return 'Χωρίς email';
    case NotificationRecipientStatus.Queued:
      return 'Σε ουρά';
    case NotificationRecipientStatus.Failed:
      return 'Απέτυχε';
    case NotificationRecipientStatus.Sent:
      return 'Στάλθηκε';
    case NotificationRecipientStatus.Responded:
      return 'Απάντησε';
    case NotificationRecipientStatus.TokenExpired:
      return 'Ληγμένο token';
    default:
      return '';
  }
}

export const notificationRecipientStatusOptions = [
  { value: NotificationRecipientStatus.NoEmail, label: 'Χωρίς email' },
  { value: NotificationRecipientStatus.Queued, label: 'Σε ουρά' },
  { value: NotificationRecipientStatus.Failed, label: 'Απέτυχε' },
  { value: NotificationRecipientStatus.Sent, label: 'Στάλθηκε' },
  { value: NotificationRecipientStatus.Responded, label: 'Απάντησε' },
  { value: NotificationRecipientStatus.TokenExpired, label: 'Ληγμένο token' },
];

/** Ετικέτες πεδίων φόρμας επικαιροποίησης, όπως αποθηκεύονται στο responsePayload */
export const responsePayloadFieldLabels: Record<string, string> = {
  name: 'Επωνυμία',
  addressLine1: 'Διεύθυνση',
  addressLine2: 'Διεύθυνση - 2η γραμμή',
  postalCode: 'T.K.',
  addressArea: 'Περιοχή',
  addressCity: 'Πόλη',
  addressNomos: 'Νομός',
  regionId: 'Περιφέρεια',
  subregionId: 'Περιφερειακή Ενότητα',
  contactPerson: 'Υπεύθυνος',
  phone: 'Τηλέφωνο',
  mobile: 'Κινητό',
  email: 'E-mail',
};

/** Μετατροπή JSON payload σε αναγνώσιμες γραμμές (grid και Excel) */
export function formatResponsePayload(payload?: string | null): string | null {
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return JSON.stringify(parsed, null, 2);
    }
    const lines = Object.entries(parsed)
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => `${responsePayloadFieldLabels[key] ?? key}: ${value}`);
    return lines.length ? lines.join('\n') : null;
  } catch {
    return payload;
  }
}
